import { markets } from "../../src/constants";

/** @private */
const getDatePlaceholderTranslation = (market: string) => {
    switch (market) { // Should maybe switch on language rather than market later on?
        case markets.estonia:
            return {
                dayPlaceholder: 'pp', // Päev
                monthPlaceholder: 'kk', // Kuu
                yearPlaceholder: 'aaaa', // Aasta
            };

        case markets.finland:
            return {
                dayPlaceholder: 'p', // Päiva
                monthPlaceholder: 'k', // Kuukaksi
                yearPlaceholder: 'vvvv', // Vuosi
            };

        case markets.austria:
        case markets.germany:
            return {
                dayPlaceholder: 'tt', // Tag
                monthPlaceholder: 'mm', // Monat
                yearPlaceholder: 'jjjj', // Jahr
            };

        case markets.norway:
        case markets.sweden:
            return {
                dayPlaceholder: 'dd', // Dag
                monthPlaceholder: 'mm', // Måned / Månad
                yearPlaceholder: 'åååå', // År
            };

        case markets.latvia:
            return {
                dayPlaceholder: 'dd', // Diena
                monthPlaceholder: 'mm', // Menesis
                yearPlaceholder: 'gggg', // Gads
            };

        case markets.lithuania:
            return {
                dayPlaceholder: 'dd', // Diena
                monthPlaceholder: 'mm', // Mėnuo
                yearPlaceholder: 'mmmm', // Metai
            };

        case markets.poland:
            return {
                dayPlaceholder: 'dd', // Dzien
                monthPlaceholder: 'mm', // Miesiac
                yearPlaceholder: 'rrrr', // Rok
            };

        case markets.spain:
            return {
                dayPlaceholder: 'dd', // Día
                monthPlaceholder: 'mm', // Mes
                yearPlaceholder: 'aaaa', // Año
            };

        default:
            return {
                dayPlaceholder: 'dd',
                monthPlaceholder: 'mm',
                yearPlaceholder: 'yyyy',
            };
    }
};

export const dateMarketFormat = (market: string) => {
    // Supported values are: yyyy, M, MM, MMM, MMMM, d, dd.
    switch (market) {
        case markets.finland:
            return 'd.M.yyyy';

        case markets.lithuania:
            return 'yyyy.MM.dd';

        case markets.sweden:
        case markets.poland:
            return 'yyyy-MM-dd';

        case markets.spain:
            return 'dd/MM/yyyy';

        default:
            return 'dd.MM.yyyy';
    }
}

export const marketRegex = (market: string) => {
    switch (market) {
        case markets.finland:
            return /^(\d|\d\d)\.(\d|\d\d)\.\d\d\d\d$/;

        case markets.lithuania:
            return /^\d\d\d\d\.\d\d\.\d\d$/;

        case markets.sweden:
        case markets.poland:
            return /^\d\d\d\d-\d\d-\d\d$/;

        case markets.spain:
            return /^\d\d\/\d\d\/\d\d\d\d$/;

        default:
            return /^\d\d\.\d\d\.\d\d\d\d$/;
    }
}

export const dateIndexPositions = (market: string) => {
    switch (market) {
        case markets.lithuania:
        case markets.poland:
        case markets.sweden:
            return {
                day: 2,
                month: 1,
                year: 0
            }

        default:
            return {
                day: 0,
                month: 1,
                year: 2
            }
    }
}

export const mainDateDelimiter = (market: string) => {
    switch (market) {
        case markets.sweden:
        case markets.poland:
            return '-';

        case markets.spain:
            return '/';

        default:
            return '.';
    }
}

interface DateModeProps {
  mode:
  | 'numberOfYearsBefore'
  | 'numberOfMonthsBefore'
  | 'numberOfDaysBefore'
  | 'numberOfYearsAfter'
  | 'numberOfMonthsAfter'
  | 'numberOfDaysAfter',
  value: number;
}

export const getDateFromProps = (date: string | undefined) => {
    if (!date) {
        return null;
    }

    if (date === 'today') {
        return new Date(new Date().toDateString());
    }

    if (typeof date === 'string' && date[0] === '{') {
      const { mode, value } = JSON.parse(date) as DateModeProps;
      const current = new Date(new Date().toDateString());
      switch (mode) {
        case 'numberOfDaysBefore':
          return new Date(current.setDate(current.getDate() - value));
        case 'numberOfDaysAfter':
          return new Date(current.setDate(current.getDate() + value));
        case 'numberOfMonthsBefore':
          return new Date(current.setMonth(current.getMonth() - value));
        case 'numberOfMonthsAfter':
          return new Date(current.setMonth(current.getMonth() + value));
        case 'numberOfYearsBefore':
          return new Date(current.setFullYear(current.getFullYear() - value));
        default:
          return new Date(current.setFullYear(current.getFullYear() + value));
      }
    }

    return new Date(date);
};

export const toUTCDate = (date: Date) =>
    new Date(date.getTime() - (date.getTimezoneOffset() * 60000));

export const utcToLocalDate = (date: Date) =>
    new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());

export const convertToDefaultLocalDate = (value: string | Date, market: string) => {
    if (!value) {
        return undefined;
    }

    if (value instanceof Date) {
        return utcToLocalDate(value);
    }

    if (typeof value === 'string' && value.length > 10 && !Number.isNaN(new Date(value))) {
        return utcToLocalDate(new Date(value));
    }

    const parts = value.toString().split( mainDateDelimiter(market) ).map((p) => parseInt(p, 10));
    const pos = dateIndexPositions(market);
    const date = new Date(parts[pos.year], parts[pos.month] - 1, parts[pos.day]);

    return Number.isNaN(date.getTime()) ? undefined : date;
};

export const dateFormatPlaceholderRemapper = (format: string, market: string) => {
    const { dayPlaceholder, monthPlaceholder, yearPlaceholder } = getDatePlaceholderTranslation(market);

    let day = 'dd';
    let month = 'MM';
    if (market === markets.finland) {
        day = 'd';
        month = 'M';
    }

    return format
        .replace(day, dayPlaceholder)
        .replace(month, monthPlaceholder)
        .replace('yyyy', yearPlaceholder);
}

export function yearsAgo(years: number): Date {
	const d = new Date()
	d.setFullYear(d.getFullYear() - years)
	return d
}

export function yearsInFuture(years: number): Date {
	const d = new Date()
	d.setFullYear(d.getFullYear() + years)
	return d
}
